import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Megaphone,
  Heart,
  Grid3x3,
  MapPinned,
  Smartphone,
  LogOut,
  Menu,
  X,
  MapPin,
  Video,
  History,
  BarChart3,
  KeyRound,
  Settings as SettingsIcon,
  Sun,
  Moon,
  Monitor,
  Users as UsersIcon,
  Gift,
  FileText,
} from "lucide-react";
import { clearToken, clearUserToken, clearStoredUser, api, getStoredUser, getUserToken } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { toast } from "sonner";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-dashboard" },
  { to: "/admin/ads", label: "Ad Config", icon: Megaphone, testId: "nav-ads" },
  { to: "/admin/ads/history", label: "Ad History", icon: History, testId: "nav-ad-history" },
  { to: "/admin/ads/video-rules", label: "Video Rules", icon: Video, testId: "nav-video-rules" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
  { to: "/admin/favorites", label: "Favorites", icon: Heart, testId: "nav-favorites" },
  { to: "/admin/categories", label: "Categories", icon: Grid3x3, testId: "nav-categories" },
  { to: "/admin/places-tester", label: "Places Tester", icon: MapPinned, testId: "nav-places-tester" },
  { to: "/admin/mobile-connection", label: "Mobile Connection", icon: Smartphone, testId: "nav-mobile-connection" },
  { to: "/admin/users", label: "Users", icon: UsersIcon, testId: "nav-users" },
  { to: "/admin/referrals", label: "Referrals", icon: Gift, testId: "nav-referrals" },
  { to: "/admin/pages", label: "Pages", icon: FileText, testId: "nav-pages" },
  { to: "/admin/secrets", label: "API Keys", icon: KeyRound, testId: "nav-secrets" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const opts = [
    { v: "light", icon: Sun, label: "Light" },
    { v: "system", icon: Monitor, label: "System" },
    { v: "dark", icon: Moon, label: "Dark" },
  ];
  return (
    <div className="px-3 pt-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1 mb-2">
        Theme
      </div>
      <div className="flex gap-1 bg-secondary rounded-lg p-1" data-testid="theme-toggle">
        {opts.map((o) => (
          <button
            key={o.v}
            onClick={() => setMode(o.v)}
            data-testid={`theme-${o.v}`}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${
              mode === o.v
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <o.icon className="w-3.5 h-3.5" />
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SidebarInner({ onNavClick, onLogout, me }) {
  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-md shadow-primary/20">
            <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              NearMe
            </div>
            <div className="text-sm font-bold leading-tight">Admin Console</div>
          </div>
        </div>
        {me && (
          <div
            className="mt-4 flex items-center gap-2 p-2 rounded-lg bg-secondary/60"
            data-testid="sidebar-user"
          >
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
              {me.picture ? (
                <img src={me.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                (me.name || me.email || "?").slice(0, 1).toUpperCase()
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold truncate">{me.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
                {me.role}
              </div>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.exact}
            data-testid={n.testId}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-accent text-accent-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="w-4 h-4 shrink-0" strokeWidth={2.2} />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <ThemeToggle />
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="mt-3 w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={2.2} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(getStoredUser());

  useEffect(() => {
    // ONLY hit /auth/me when we actually have a JWT session token. Legacy
    // X-Admin-Token admins have no user object and this endpoint 401s —
    // which was triggering an unwanted redirect to /login (auto-logout bug).
    if (!getUserToken()) return;
    api
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => setMe(getStoredUser()));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side logout failure is non-blocking — we always clear local state.
      console.warn("Backend logout failed (continuing with local sign-out):", err);
    }
    clearToken();
    clearUserToken();
    clearStoredUser();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card sticky top-0 h-screen">
        <div className="w-full">
          <SidebarInner onLogout={logout} me={me} />
        </div>
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-card h-full shadow-2xl animate-in-up">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-secondary"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarInner onNavClick={() => setOpen(false)} onLogout={logout} me={me} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 glass border-b border-border px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            className="p-2 rounded-lg hover:bg-secondary"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="text-sm font-bold">NearMe Admin</span>
          </div>
          <div className="w-9" />
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
